import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link, Outlet } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import * as common from '../../commons/common';
import { useIsLoginState } from '../../components/IsLoginContext';
import useRoleBasedAccess from "../../commons/useRoleBasedAccess";
import UNIlogo from '../../images/UNI_LOGO_BLUE_SVG.svg';
import OrgChartDialog from './org/OrgChartDialog';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Button,
    Divider,
    Collapse,
    ListItemIcon,
    IconButton,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
// icons
import MenuIcon from '@mui/icons-material/Menu';
import ListItemButton from '@mui/material/ListItemButton';
import AddchartIcon from '@mui/icons-material/Addchart';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DialpadOutlinedIcon from '@mui/icons-material/DialpadOutlined';
import DvrIcon from '@mui/icons-material/Dvr';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { BorderRight, BuildOutlined } from '@mui/icons-material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';

const ManagerLayout = ({ children }) => {

    useRoleBasedAccess(['MANAGE', 'MANAGER']);

    const location = useLocation();
    const navigate = useNavigate();
    const { user, setIsLogin } = useIsLoginState();

    const [open, setOpen] = useState(true);
    const [openMenus, setOpenMenus] = useState(false); //서브메뉴 open
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [orgOpen, setOrgOpen] = useState(false);

    const isKhuDomain = window.location.hostname === 'khu.unicheck.co.kr';

    const menuItems = [
        // { text: '대시보드', subItems: ['시설별', '인원별'], path: 'dashboard', icon: <AddchartIcon /> },
        { text: '대시보드', path: 'dashboard/facility', icon: <AddchartIcon /> },
        { text: '시설 설정', path: 'facility-info', icon: <DvrIcon /> },
        // { text: '시설 점검 현황', subItems: ['점검현황', '미점검현황'], path: 'status', icon: <DialpadOutlinedIcon /> },
        { text: '시설 점검 현황', path: 'status/check', icon: <DialpadOutlinedIcon /> },
        { text: '고장 A/S 현황', path: 'as', icon: <BuildOutlined /> },
        { text: '고객의 소리', path: 'customer-voice', icon: <SentimentSatisfiedAltOutlinedIcon /> },
        { text: '보고서', path: 'report', icon: <InventoryOutlinedIcon /> },
        isKhuDomain
            ? { text: '게시판', subItems: ['정기검사', '안전수칙'], path: 'bulletin-board', icon: <FormatListBulleted /> }
            : { text: '자료정보실', path: 'bulletin-board/regularInspection', icon: <FormatListBulleted /> },
        { text: '점검 강제등록', path: 'force-register', icon: <WarningAmberIcon /> },
    ];

    const ScrollToTop = () => {
        window.scrollTo(0, 0);
    };

    const handleDrawer = useCallback(() => {
        setOpen(prevState => !prevState);
        setOpenMenus(prevState => !prevState);
    }, []);

    const handleMenuClick = useCallback((item) => {
        if (item.subItems) {
            setOpen(true);
            setOpenMenus(prevState => ({
                ...Object.fromEntries(Object.entries(prevState).map(([key]) => [key, false])),
                [item.text]: !prevState[item.text]
            }));
        } else {
            setSelectedMenu(item.text);
            setOpenMenus(prevState => ({
                ...Object.fromEntries(Object.entries(prevState).map(([key]) => [key, false]))
            }));
            if (item.path) {
                ScrollToTop();
                navigate(`/manager/${item.path}`);
            }
        }
    }, [navigate]);

    const handleSubMenuClick = useCallback((item, subItem) => {
        ScrollToTop();
        if (item.text === '시설 점검 현황') {
            navigate(`/manager/status/${subItem === '점검현황' ? 'check' : 'uncheck'}`);
        } else if (item.text === '게시판') {
            navigate(`/manager/bulletin-board/${subItem === '정기검사' ? 'regularInspection' : 'guides'}`);
        }
    }, [navigate]);

    useEffect(() => {
        let activeMenu = menuItems.find(item => location.pathname.includes(item.path));
        let foundSubItem = null;

        if (activeMenu) {
            setSelectedMenu(activeMenu.text); // 상위 메뉴 강조
            if (activeMenu.subItems) {
                foundSubItem = activeMenu.subItems.find(subItem => {
                    const subPath = activeMenu.text === '시설 점검 현황'
                        ? `/manager/status/${subItem === '점검현황' ? 'check' : 'uncheck'}`
                        : `/manager/bulletin-board/${subItem === '정기검사' ? 'regularInspection' : 'guides'}`;
                    return location.pathname.includes(subPath);
                });
                if (foundSubItem) {
                    setSelectedMenu(foundSubItem); // 서브메뉴 강조
                    setOpenMenus(prevState => ({
                        ...Object.fromEntries(Object.entries(prevState).map(([key]) => [key, false])),
                        [activeMenu.text]: true  // 현재 활성화된 서브메뉴 열기
                    }));
                } else {
                    setOpenMenus(prevState => ({
                        ...Object.fromEntries(Object.entries(prevState).map(([key]) => [key, false]))
                    }));
                }
            } else {
                setOpenMenus(prevState => ({
                    ...Object.fromEntries(Object.entries(prevState).map(([key]) => [key, false]))
                }));
            }
        }
    }, [location.pathname]);


    const handleLogout = async () => {
        const url = `${common.getApiUrl()}/auth/logout`;
        const accessToken = localStorage.getItem('access_token');

        try {
            const response = await axios.post(url, {}, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setIsLogin(false);
            navigate('/', { replace: true });
        } catch (error) {
            common.handleApiError(error);
        }
    };

    const drawerWidth = 220;
    const drawerHeight = 65;

    const openedMixin = {
        width: drawerWidth,
        overflowX: 'hidden',
        borderRight: 0,
        display: 'flex',
        flexDirection: 'column',
    };

    const closedMixin = {
        overflowX: 'hidden',
        width: 56,
        display: 'flex',
        flexDirection: 'column',
    };

    const DrawerHeader = styled('div')({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '0 8px',
        minHeight: `${drawerHeight}px`,
        height: `${drawerHeight}px`,
        lineHeight: `${drawerHeight}px`,
        boxSizing: 'border-box',
        borderRight: '0',
    });

    const AppBar = styled(MuiAppBar, {
        shouldForwardProp: (prop) => prop !== 'open',
    })(({ open }) => ({
        zIndex: 1300,
        marginLeft: open ? drawerWidth : 56,
        lineHeight: `${drawerHeight}px`,
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: 'none',
        boxSizing: 'border-box',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        color: '#707070',
        width: `calc(100% - ${open ? drawerWidth : 56}px)`
    }));

    const Drawer = styled(MuiDrawer, {
        shouldForwardProp: (prop) => prop !== 'open',
    })(({ open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        border: 'none',
        ...(open && {
            ...openedMixin,
            '& .MuiDrawer-paper': openedMixin,
        }),
        ...(!open && {
            ...closedMixin,
            '& .MuiDrawer-paper': closedMixin,
        })
    }));

    const logo = useMemo(() => (
        <Box
            component="img"
            src={UNIlogo}
            alt="KyungHeeUniversity Logo"
            sx={{
                maxWidth: '80px',
                height: 'auto',
                marginLeft: '10px',
                cursor: 'pointer',
            }}
            onClick={() => navigate('/manager/dashboard/facility')}
        />
    ), []);

    return (
        <>
            <Helmet>
                <style>{'body { background-color: var(--main-white-color); }'}</style>
            </Helmet>
            <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                <AppBar position="fixed" open={open} >
                    <Toolbar sx={{ height: '65px', lineHeight: '65px', minHeight: '65px !important', boxSizing: 'border-box' }}>
                        <Typography variant='subtitle2' noWrap component="div" sx={{ color: '#000' }}>관리자</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                            <Link to="/manager/personal-info" style={{ textDecoration: 'none', display: 'flex' }}>
                                <AccountCircleOutlinedIcon sx={{ color: 'var(--main-blue-color)', marginRight: '5px' }} />
                            </Link>
                            <Typography variant="body2" sx={{ mr: 2 }} >
                                {user ? `${user.memberNm}님 안녕하세요` : '관리자님 안녕하세요'}
                            </Typography>
                            <Button color="inherit"
                                    sx={{ ml: 1, fontSize: '0.8rem' }}
                                    onClick={handleLogout}>
                                로그아웃
                            </Button>
                        </Box>
                    </Toolbar>
                </AppBar>
                <Drawer variant="permanent" open={open}>
                    <DrawerHeader>
                        <IconButton onClick={handleDrawer}>
                            <MenuIcon />
                        </IconButton>
                        {<Box
                            component="img"
                            src={UNIlogo}
                            alt="KyungHeeUniversity Logo"
                            sx={{
                                maxWidth: '80px',
                                height: 'auto',
                                marginLeft: '10px',
                                cursor: 'pointer',
                            }}
                            onClick={() => navigate('/manager/dashboard/facility')}
                        />}
                    </DrawerHeader>
                    <Divider />
                    <List sx={{ padding: '13px 0', flex: 1, overflowY: 'auto' }}>
                        {menuItems.map((item, index) => (

                            <ListItem key={index} disablePadding sx={{ display: 'block' }}>
                                <ListItemButton
                                    sx={{
                                        minHeight: 48,
                                        justifyContent: open ? 'initial' : 'center',
                                        color: selectedMenu === item.text ? 'var(--main-blue-color)' : null
                                    }}
                                    onClick={() => handleMenuClick(item)}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 1 : 'auto',
                                            color: selectedMenu === item.text ? 'var(--main-blue-color)' : null,
                                            justifyContent: 'left',
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
                                    {item.subItems && open ?
                                        (openMenus[item.text] ? <ExpandLess sx={{ color: '#CCCCCC' }} /> : <ExpandMore sx={{ color: 'var(--sub-darkgrey-color)' }} />) : null}
                                </ListItemButton>
                                {item.subItems && (
                                    <Collapse in={openMenus[item.text]} unmountOnExit={true}>
                                        <Box sx={{ mb: 2 }}>
                                            <List component="div" disablePadding>
                                                {item.subItems.map((subItem, subIndex) => (
                                                    <ListItemButton
                                                        key={subIndex}
                                                        sx={{
                                                            pl: 6,
                                                            height: '36px',
                                                            color: selectedMenu === subItem ? 'var(--main-blue-color)' : null
                                                        }}
                                                        onClick={() => handleSubMenuClick(item, subItem)}
                                                    >
                                                        <ListItemText
                                                            disableTypography
                                                            primary={subItem}
                                                            sx={{ fontSize: '0.9rem', fontFamily: 'Pretendard-Regular' }}
                                                        />
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        </Box>
                                    </Collapse>
                                )}
                            </ListItem>
                        ))}
                    </List>

                    {/* 하단 고정 – 조직관리 아이콘 */}
                    <Divider />
                    <List sx={{ padding: '4px 0' }}>
                        <ListItem disablePadding sx={{ display: 'block' }}>
                            <Tooltip title={open ? '' : '조직관리'} placement="right" arrow>
                                <ListItemButton
                                    onClick={() => setOrgOpen(true)}
                                    sx={{
                                        minHeight: 48,
                                        justifyContent: open ? 'initial' : 'center',
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 1 : 'auto',
                                            justifyContent: 'left',
                                            color: '#555',
                                        }}
                                    >
                                        <AccountTreeOutlinedIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="조직관리" sx={{ opacity: open ? 1 : 0 }} />
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    </List>
                </Drawer>

                {/* 조직도 팝업 */}
                <OrgChartDialog open={orgOpen} onClose={() => setOrgOpen(false)} />

                {/* Main Content Area */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <DrawerHeader />
                    <Box sx={{ p: 3, marginLeft: open ? `${drawerWidth}px` : '56px', flex: 1 }}>
                        <Outlet />
                    </Box>

                    {/* Footer 비활성화 */}
                    {/* <Box
                        component="footer"
                        sx={{
                            backgroundColor: '#f8f9fa',
                            borderTop: '1px solid #e9ecef',
                            padding: '30px 20px',
                            marginLeft: open ? `${drawerWidth}px` : '56px',
                            marginTop: 'auto'
                        }}
                    >
                        <Box sx={{ maxWidth: '1200px', margin: 0 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#676767',
                                    lineHeight: 1.6,
                                    fontSize: '0.85rem'
                                }}
                            >
                                (주) 디에스티인터내셔날 | 사업자등록번호 : 120-86-09672 <br/>
                                주소 : 서울시 금천구 가산디지털 1로 149 (가산동 371-59) 신한이노플렉스 12층 (우편번호: 08506)<br/>
                                TEL : 02-554-1828 / FAX : 02-554-1773 | E-mail : dsti@dsti.co.kr<br/>
                                Copyright © DSTI Co.,Ltd. All rights reserved.
                            </Typography>
                        </Box>
                    </Box> */}
                </Box>
            </Box>
        </>
    );
};

export default ManagerLayout;